import * as React from 'react';
import * as Modal from 'react-modal';
import * as classnames from 'classnames';

import {
	Button,
	Icon,
	Tabs,
	TabOption,
	Select,
	SelectOption,
} from '../util';
import {
	getProducts,
	getExposuresForProduct,
	getPerilsForExposure,
	ModificationData,
} from './_util';

interface Props {
	isOpen: boolean;
	files: ConfigEditor.AssetFiles;
	onClose(): void;
	onModifyAssets(type: ConfigEditor.ModificationType, data: ModificationData, changeDisplay: ChangeModalDisplay): void;
}

interface State {
	modificationType: ConfigEditor.ModificationType;
	modificationEntity: ConfigEditor.ModificationEntity;
	modification: ConfigEditor.AssetsModification;
	successMessage: string;
}

const MODIFICATION_TYPE_OPTIONS: TabOption[] = [
	{ label: 'Add configuration', value: 'ADD_ASSETS' },
	{ label: 'Remove configuration', value: 'REMOVE_ASSETS' },
];

const MODIFICATION_ENTITY_OPTIONS: TabOption[] = [
	{ label: 'Product', value: 'PRODUCT' },
	{ label: 'Exposure', value: 'EXPOSURE' },
	{ label: 'Peril', value: 'PERIL'},
];

const INITIAL_STATE: State = {
	modificationType: 'ADD_ASSETS',
	modificationEntity: 'PRODUCT',
	modification: {
		productName: '',
		exposureName: '',
		perilName: '',
	},
	successMessage: '',
};

export default class ModifyAssetsModal extends React.Component<Props, State> {

	public state = INITIAL_STATE;

	constructor(props: Props, context: any) {
		super(props, context);
		this.handleSelectModificationType = this.handleSelectModificationType.bind(this);
		this.handleSelectModificationEntity = this.handleSelectModificationEntity.bind(this);
		this.handleInputProduct = this.handleInputProduct.bind(this);
		this.handleSelectProduct = this.handleSelectProduct.bind(this);
		this.handleInputExposure = this.handleInputExposure.bind(this);
		this.handleSelectExposure = this.handleSelectExposure.bind(this);
		this.handleInputPeril = this.handleInputPeril.bind(this);
		this.handleSelectPeril = this.handleSelectPeril.bind(this);
		this.handleConfirmModification = this.handleConfirmModification.bind(this);
		this.handleConfirmModificationAndClose = this.handleConfirmModificationAndClose.bind(this);
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (!nextProps.isOpen) {
			this.setState({...INITIAL_STATE});
		}
	}

	public render() {
		return <Modal
			isOpen={this.props.isOpen}
			onRequestClose={this.props.onClose}
			style={this.getZeroPaddingStyle()}
		>
			{this.renderContent()}
		</Modal>;
	}

	private renderContent() {
		return <div className='modify-assets-modal-content'>
			<a className='close-button' onClick={this.props.onClose}>
				<Icon name='close' />
			</a>
			<label className='label-bold'>Add/Remove Configuration</label>
			{this.renderModificationTypes()}
			{this.renderModificationEntities()}
			{this.renderForm()}
			{this.renderNavigation()}
			{this.renderSuccessMessage()}
		</div>;
	}

	private renderModificationTypes() {
		return <Tabs
			options={MODIFICATION_TYPE_OPTIONS}
			contentLabel='modification-type'
			selectedOption={this.state.modificationType}
			title='Choose modification type:'
			onSelect={this.handleSelectModificationType}
		/>;
	}

	private renderModificationEntities() {
		const title = this.state.modificationType === 'ADD_ASSETS' ? 'Add:' : 'Remove:';
		return <Tabs
			options={MODIFICATION_ENTITY_OPTIONS}
			contentLabel='modification-entity'
			selectedOption={this.state.modificationEntity}
			title={title}
			onSelect={this.handleSelectModificationEntity}
		/>;
	}

	private renderForm() {
		return <div className='modification-form'>
			{this.renderProduct()}
			{this.renderExposure()}
			{this.renderPeril()}
		</div>;
	}

	private renderProduct() {
		const { modificationType, modificationEntity, modification: {productName} } = this.state;
		if (modificationType === 'ADD_ASSETS' && modificationEntity === 'PRODUCT') {
			const tooltip = this.getErrorTooltip('PRODUCT');
			const inputClassName = tooltip ? 'input-field--error' : 'input-field';
			return <div
				className='modification-form-field asset-tooltip'
				data-tooltip={tooltip}
			>
				<label>Product name:</label>
				<input
					type='text'
					value={productName}
					onChange={this.handleInputProduct}
					className={inputClassName}
				/>
			</div>;
		} else {
			const allowedOptions = this.getAllowedProductOptions();
			return <Select
				allowedOptions={allowedOptions}
				selectedOption={this.state.modification.productName}
				onChange={this.handleSelectProduct}
				title='Select product:'
				containerClassName='modification-form-field'
				disabled={false}
			/>;
		}
	}

	private renderExposure() {
		const { modificationType, modificationEntity, modification: {exposureName} } = this.state;
		if (modificationType === 'ADD_ASSETS' && modificationEntity !== 'PERIL') {
			const tooltip = this.getErrorTooltip('EXPOSURE');
			const inputClassName = tooltip ? 'input-field--error' : 'input-field';
			return <div
				className='modification-form-field asset-tooltip'
				data-tooltip={tooltip}
			>
				<label>Exposure name:</label>
				<input
					type='text'
					value={exposureName}
					onChange={this.handleInputExposure}
					className={inputClassName}
				/>
			</div>;
		} else {
			const allowedOptions = this.getAllowedExposureOptions();
			const containerClassName = classnames(
				'modification-form-field',
				this.getViewableClass('EXPOSURE'),
			);
			return <Select
				allowedOptions={allowedOptions}
				selectedOption={this.state.modification.exposureName}
				onChange={this.handleSelectExposure}
				title='Select exposure:'
				containerClassName={containerClassName}
				disabled={!this.isExposureSelectorEnabled()}
			/>;
		}
	}

	private renderPeril() {
		const { modificationType, modification: {perilName} } = this.state;
		if (modificationType === 'ADD_ASSETS') {
			const tooltip = this.getErrorTooltip('PERIL');
			const inputClassName = tooltip ? 'input-field--error' : 'input-field';
			return <div
				className='modification-form-field asset-tooltip'
				data-tooltip={tooltip}
			>
				<label>Peril name:</label>
				<input
					type='text'
					value={perilName}
					onChange={this.handleInputPeril}
					className={inputClassName}
				/>
			</div>;
		} else {
			const allowedOptions = this.getAllowedPerilOptions();
			const containerClassName = classnames(
				'modification-form-field',
				this.getViewableClass('PERIL'),
			);
			return <Select
				allowedOptions={allowedOptions}
				selectedOption={this.state.modification.perilName}
				onChange={this.handleSelectPeril}
				title='Select peril:'
				containerClassName={containerClassName}
				disabled={!this.isPerilSelectorEnabled()}
			/>;
		}
	}

	private renderNavigation() {
		return <div className='modify-assets-modal-navigation'>
			<div>
				{this.renderConfirmAndCloseButton()}
				{this.renderConfirmButton()}
				<Button
					className='navigation-button'
					color='link'
					onClick={this.props.onClose}
				>
					Cancel
				</Button>
			</div>
		</div>;
	}

	private renderSuccessMessage() {
		const successMessage: string = this.state.successMessage;
		if (successMessage) {
			return <div className='success-message'>
				<Icon name='check-circle'/>
				{successMessage}
			</div>;
		} else {
			return <div className='success-message-placeholder'/>;
		}
	}

	private renderConfirmButton() {
		return <button
			className='button navigation-button'
			onClick={this.handleConfirmModification}
			disabled={!this.canConfirmModification()}
		>
			Confirm
		</button>;
	}

	private renderConfirmAndCloseButton() {
		return <button
			className='button navigation-button'
			onClick={this.handleConfirmModificationAndClose}
			disabled={!this.canConfirmModification()}
		>
			Confirm and Close
		</button>;
	}

	private handleSelectModificationType(selectedType: ConfigEditor.ModificationType) {
		this.setState({
			...INITIAL_STATE,
			modificationType: selectedType,
		});
	}

	private handleSelectModificationEntity(selectedEntity: ConfigEditor.ModificationEntity) {
		this.setState({
			...INITIAL_STATE,
			modificationType: this.state.modificationType,
			modificationEntity: selectedEntity,
		});
	}

	private handleInputProduct(event: React.ChangeEvent<HTMLInputElement>) {
		const productName: string = (event.currentTarget as HTMLInputElement).value;
		this.setState({
			modification: {
				...this.state.modification,
				productName,
			},
		});
	}

	private handleSelectProduct(productName: string) {
		this.setState({
			modification: {
				...INITIAL_STATE.modification,
				productName,
			},
		});
	}

	private handleInputExposure(event: React.ChangeEvent<HTMLInputElement>) {
		const exposureName: string = (event.currentTarget as HTMLInputElement).value;
		this.setState({
			modification: {
				...this.state.modification,
				exposureName,
			},
		});
	}

	private handleSelectExposure(exposureName: string) {
		this.setState({
			modification: {
				...this.state.modification,
				exposureName,
				perilName: '',
			},
		});
	}

	private handleInputPeril(event: React.ChangeEvent<HTMLInputElement>) {
		const perilName: string = (event.currentTarget as HTMLInputElement).value;
		this.setState({
			modification: {
				...this.state.modification,
				perilName,
			},
		});
	}

	private handleSelectPeril(perilName: string) {
		this.setState({
			modification: {
				...this.state.modification,
				perilName,
			},
		});
	}

	private handleConfirmModification() {
		const { modificationType, modificationEntity, modification } = this.state;
		const data: ModificationData = {
			modifying: modificationEntity,
			modification,
		};
		this.setState({
			...INITIAL_STATE,
			modificationType,
			modificationEntity,
			successMessage: this.getSuccessMessage(),
		});
		this.props.onModifyAssets(modificationType, data, 'KEEP_MODAL_OPEN');
	}

	private handleConfirmModificationAndClose() {
		const { modificationType, modificationEntity, modification } = this.state;
		const data: ModificationData = {
			modifying: modificationEntity,
			modification,
		};
		this.props.onModifyAssets(modificationType, data, 'CLOSE_MODAL');
	}

	private getAllowedProductOptions(): SelectOption[] {
		const productNames = getProducts(this.props.files);
		return productNames.map(productName => {
			return { label: productName, value: productName };
		});
	}

	private getAllowedExposureOptions(): SelectOption[] {
		const { modification: { productName } } = this.state;
		const exposureNames = productName ? getExposuresForProduct(productName, this.props.files) : [];
		return exposureNames.map(exposureName => {
			return { label: exposureName, value: exposureName };
		});
	}

	private getAllowedPerilOptions(): SelectOption[] {
		const { modification: { productName, exposureName }} = this.state;
		const perilNames = productName && exposureName ?
			getPerilsForExposure(productName, exposureName, this.props.files) : [];
		return perilNames.map(perilName => {
			return { label: perilName, value: perilName };
		});
	}

	private getZeroPaddingStyle() {
		return {
			content: {
				padding: 0,
				minWidth: '550px',
			},
		};
	}

	private getViewableClass(field: 'EXPOSURE' | 'PERIL') {
		return this.isFieldAvailableForDisplay(field) ? '' : 'hidden-field';
	}

	private isExposureSelectorEnabled() {
		const { modification: {productName}} = this.state;
		if (this.isFieldAvailableForDisplay('EXPOSURE')) {
			return !!productName;
		} else {
			return false;
		}
	}

	private isPerilSelectorEnabled() {
		const { modification: {productName, exposureName}} = this.state;
		if (this.isFieldAvailableForDisplay('PERIL')) {
			return !!productName && !!exposureName;
		} else {
			return false;
		}
	}

	private isFieldAvailableForDisplay(field: 'EXPOSURE' | 'PERIL') {
		const { modificationEntity, modificationType } = this.state;
		if (modificationType === 'ADD_ASSETS') {
			return true;
		} else if (field === 'EXPOSURE') {
			return modificationEntity === 'EXPOSURE' || modificationEntity === 'PERIL';
		} else {
			return modificationEntity === 'PERIL';
		}
	}

	private canConfirmModification() {
		const { modificationType, modificationEntity, modification } = this.state;
		const { productName, exposureName, perilName } = modification;
		if (modificationType === 'ADD_ASSETS') {
			return !!productName
				&& !!exposureName
				&& !!perilName
				&& !this.getErrorMessage();
		} else {
			switch (modificationEntity) {
				case 'PRODUCT': return !!productName;
				case 'EXPOSURE': return !!productName && !!exposureName;
				case 'PERIL': return !!productName && !!exposureName && !!perilName;
				default: return false;
			}
		}
	}

	private getErrorMessage(): string {
		const { modificationType, modificationEntity, modification } = this.state;
		const { productName, exposureName, perilName } = modification;
		const errorSuffix = 'of that name already exists';
		if (modificationType === 'REMOVE_ASSETS') {
			return '';
		} else {
			switch (modificationEntity) {
				case 'PRODUCT':
					return this.isInvalidName(productName, this.getAllowedProductOptions())
						? `A product ${errorSuffix}` : '';
				case 'EXPOSURE':
					return this.isInvalidName(exposureName, this.getAllowedExposureOptions())
						? `An exposure ${errorSuffix}` : '';
				case 'PERIL':
					return this.isInvalidName(perilName, this.getAllowedPerilOptions())
						? `A peril ${errorSuffix}` : '';
				default:
					return '';
			}
		}
	}

	private getSuccessMessage(): string {
		const { modificationType, modificationEntity } = this.state;
		const successPrefix: string = modificationType === 'ADD_ASSETS'
			? 'Successfully added' : 'Successfully removed';
		return `${successPrefix} ${modificationEntity.toLowerCase()}`;
	}

	private isInvalidName(name: string, unavailableOptions: SelectOption[]) {
		if (!name) {
			return false;
		} else {
			const unavailableNames = unavailableOptions.map(o => o.value);
			return unavailableNames.findIndex(n => n.toLowerCase() === name.toLowerCase()) !== -1;
		}
	}

	private getErrorTooltip(modificationEntity: ConfigEditor.ModificationEntity): string {
		const errorMessage = this.getErrorMessage();
		if (this.state.modificationEntity === modificationEntity && errorMessage) {
			return errorMessage;
		} else {
			return null;
		}
	}

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./ts/components/config-editor/ModifyAssetsModal.tsx